<template>
  <div class="orders-to-fulfill" @mousemove="noteInteraction" @keydown="noteInteraction" @scroll.capture="noteInteraction" @click="noteInteraction">
    <!-- Neuzugänge (Auto-Import) werden gepuffert statt live eingefügt — sonst springt die Liste beim Anhaken -->
    <div v-if="pendingNewUids.length && !archived" class="new-orders-bar">
      <v-icon small color="#6a4fc4" class="mr-1">mdi-tray-arrow-down</v-icon>
      <span>{{ pendingNewUids.length }} neue {{ pendingNewUids.length === 1 ? 'Bestellung' : 'Bestellungen' }} seit dem Laden</span>
      <v-btn x-small depressed rounded color="#7b5cd6" dark class="ml-3" @click="mergePendingNew">Einblenden</v-btn>
      <span class="new-orders-bar__hint">Werden automatisch eingeblendet, wenn du zum Tab zurückkehrst oder 5 Minuten nichts passiert.</span>
    </div>
    <div
      :class="{ 'control-header--hide': !selectedOrders.length }"
      class="control-header"
    >
      <div class="control-header__selected-items">
        Selected {{ selectedOrders.length }} Orders
      </div>
      <!-- Archiv: nur ein Flag an der Bestellung (fulfillArchived), Status und Logik bleiben unverändert -->
      <v-btn v-if="!archived" text class="control-header__textBtn" :loading="archiving" @click="archiveSelected">
        <v-icon small left>mdi-archive-arrow-down-outline</v-icon>Archivieren
      </v-btn>
      <v-btn v-else text class="control-header__textBtn" :loading="archiving" @click="unarchiveSelected">
        <v-icon small left>mdi-archive-arrow-up-outline</v-icon>Wiederherstellen
      </v-btn>
      <v-btn @click="checkAndFulfillSelectedOrders" width="300px"
      >Fulfill</v-btn
      >
    </div>
    <v-data-table
      :show-expand="true"
      :headers="headers"
      :items="sortedOrdersToFulfill"
      :loading="loadingOrders || initialPhase"
      loading-text="Bestellungen werden geladen…"
      disable-pagination
      :hide-default-footer="true"
      show-select
      v-model="selectedOrders"
      item-key="uid"
      :single-select="false"
      :item-class="getRowClass"
    >
      <template v-slot:item.orderNote="{ item }">
        <div class="d-flex">
          <OrderNoteTooltip :order="item" />
          <SupportDialog :order-id="item.uid"/>
        </div>
      </template>
      <template v-slot:item.orderName="{ item }">
        <a :class="{'express-order-name': item.isExpress}" class="order-name-cell" @click="() => openOrder(item.uid)">
          <!-- Hold-Icons VOR dem Namen, Zelle bleibt einzeilig -->
          <v-icon
            v-if="item.fulfillHold"
            small
            color="#f0a92e"
            :title="'Fulfillment angehalten (Hold)' + (item.fulfillHold.by && item.fulfillHold.by.name ? ' von ' + item.fulfillHold.by.name : '')"
          >mdi-pause-circle</v-icon>
          <!-- Support-/Adress-Hold: blockiert den Auto-Fulfill genauso; Grund im Hover -->
          <v-icon
            v-else-if="item.supportPending || item.resolveOnNewAddress"
            small
            color="#f0a92e"
            :title="holdReason(item)"
          >{{ item.resolveOnNewAddress ? 'mdi-map-marker-alert-outline' : 'mdi-pause-circle' }}</v-icon>
          <span>{{ item.orderName }}</span>
          <v-icon v-if="item.isExpress" small color="#b4b412">mdi-flash</v-icon>
        </a>
      </template>
      <template v-slot:item.orderDate="{ item }">
        <div class="age-cell">
          <div class="d-flex align-center">
            <FeedbackTooltip :key="task.uid" v-for="task of item.tasks" :feedback="task.feedback"/>
            <TimeIndicator :creation-date="item.orderDate" />
          </div>
          <div v-if="item.resultsSentDate" class="age-cell__mail" title="Vorschau-Mail verschickt"><v-icon x-small>mdi-email-outline</v-icon> {{ dateFromNow(item.resultsSentDate) }}</div>
        </div>
      </template>
      <template v-slot:item.address="{ item }">
        <div class="addr-cell" title="Auf der Karte prüfen" @click="openAddressMap(item)">
          <div class="addr-cell__line1">
            <b>{{ item.shippingAddress.firstName }} {{ item.shippingAddress.lastName }}</b>
            <span v-if="item.shippingAddress.company" class="addr-cell__muted"> · {{ item.shippingAddress.company }}</span>
          </div>
          <div class="addr-cell__line2">
            <span>{{ item.shippingAddress.addressPrimary }}<template v-if="item.shippingAddress.addressSecondary">, {{ item.shippingAddress.addressSecondary }}</template></span>
            <v-icon v-if="noHouseNumber(item)" x-small color="#b26a00" title="Keine Hausnummer erkannt">mdi-alert-outline</v-icon>
            <span class="addr-cell__sep">·</span>
            <span>{{ item.shippingAddress.postCode }} {{ item.shippingAddress.city }}</span>
            <span class="addr-cell__cc">{{ (item.shippingAddress.countryCode || item.countryCode || "").toUpperCase() }}</span>
          </div>
        </div>
      </template>
      <template v-slot:item.action="{ item }">
        <div class="d-flex justify-end align-center">
          <v-icon class="mr-5" @click="() => addressChecked(item)"
          >{{item.shippingAddress.checked ? 'mdi-eye-check' : 'mdi-eye-outline'}}</v-icon
          >
          <v-icon class="mr-5" title="Adresse prüfen und bearbeiten" @click="() => openAddressMap(item)"
            >mdi-pencil</v-icon
          >
          <v-icon @click="(e) => openItemMenu(e, item.uid)">mdi-dots-vertical</v-icon>
        </div>
      </template>
      <template v-slot:expanded-item="{ item }">
        <td :colspan="headers.length">
          <OrderLineItems :order-tasks="item.tasks" />
        </td>
      </template>
    </v-data-table>
    <ListMenu :show="showMenu" :x="x" :y="y" :list-items="menuItems" />
    <AddressMapDialog
      :show="!!mapOrder"
      :order="mapOrderLive"
      @close="mapOrder = null"
      @toggle-checked="addressChecked"
    />
    <EditOrderAddressDialog
      v-if="showEditOrderDialog"
      :show="showEditOrderDialog"
      :order="orderToEdit"
      :on-close="closeEditOrderDialog"
    />
    <ConfirmDialog
      :show="showWarnDialog"
      :message="warnDialogMessage"
      accept-text="Fulfill Orders"
      accept-color="warning"
      icon="mdi-alert"
      :title="warnDialogTitle"
      :on-accept="fulfillSelectedOrders"
      :on-cancel="closeWarnDialog"
    />
    <IsFulfillingDialog
      v-if="showIsFulfillingDialog"
      :orders-to-fulfill="selectedOrders"
      :show="showIsFulfillingDialog"
      :on-close="closeIsFulfillingDialog"
    />
    <OrderMailDialog
        v2
        :on-sent="markOrderForAwaitingAddress"
        v-if="showMailDialog"
        :show="showMailDialog"
        :on-close="() => (this.showMailDialog = false)"
        :order-id="selectedOrderId"
        mail-category="FULFILL"
    />
    <ManualFulfillDialog :order-id="selectedOrderId" :onClose="closeManualFulfillmentDialog" :show="showManualFulfillmentDialog" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { EventBus, events } from "../../../../event-bus";
import TimeIndicator from "@/components/TaskCards/components/TimeIndicator";
import ConfirmDialog from "@/components/ConfirmDialog";
import EditOrderAddressDialog from "@/components/FulfillOrders/components/EditOrderAddressDialog";
import IsFulfillingDialog from "@/components/FulfillOrders/components/IsFulfillingDialog";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../../../../fb";
import { orderStatus } from "@/constants";
import OrderLineItems from "@/components/FulfillOrders/components/OrderLineItems";
import ListMenu from "@/components/ListMenu";
import ManualFulfillDialog from "@/components/FulfillOrders/components/ManualFulfillDialog";
import { getMailTemplatesFulfill } from "@/content/mails";
import OrderNoteTooltip from "@/components/OrderNoteTooltip";
import {getDateFromNow, openOrderModal} from "@/utils";
import FeedbackTooltip from "@/components/FeedbackTooltip";
import SupportDialog from "@/components/Support/SupportTooltip";
import OrderMailDialog from "@/components/OrderMailDialog.vue";
import AddressMapDialog from "@/components/FulfillOrders/components/AddressMapDialog.vue";

export default {
  name: "OrdersToFulfill",
  props: {
    /** Archiv-Ansicht: zeigt nur Bestellungen mit fulfillArchived, ohne Puffer */
    archived: { type: Boolean, default: false },
  },
  components: {
    AddressMapDialog,
    OrderMailDialog,
    SupportDialog,
    FeedbackTooltip,
    OrderNoteTooltip,
    ManualFulfillDialog,
    ListMenu,
    OrderLineItems,
    IsFulfillingDialog,
    EditOrderAddressDialog,
    ConfirmDialog,
    TimeIndicator,
  },
  data() {
    return {
      /** Sichtbare Bestellungen (uid → true); Neuzugänge warten in pendingNewUids */
      displayedUids: null,
      pendingNewUids: [],
      recentlyAddedUids: {},
      lastInteractionAt: Date.now(),
      autoMergeTimer: null,
      /** Erstladephase: bis der Store den Erst-Snapshot komplett verarbeitet hat (ordersToFulfillReady) — solange alles übernehmen */
      initialPhase: true,
      initialDeadline: null,
      archiving: false,
      // Adress-Prüfung auf der Karte (uid der Bestellung, Daten live aus dem Store)
      mapOrder: null,
      selectedOrders: [],
      showWarnDialog: false,
      showEditOrderDialog: false,
      showIsFulfillingDialog: false,
      orderToEdit: null,
      showMenu: false,
      showManualFulfillmentDialog: false,
      selectedOrderId: '',
      showMailDialog: false,
      x: 0,
      y: 0,
      menuItems: [
        {
          icon: "mdi-printer",
          onClick: this.showManualFulfillDialog,
          label: "Add Manual Fulfillment",
        },
        {
          icon: "mdi-email-outline",
          onClick: () => {
            this.showMailDialog = true;
          },
          label: "Send Mail",
        },
        {
          icon: "mdi-email-search-outline",
          onClick: this.searchOrderInMails,
          label: "Search In Mails",
        },
      ],
      warnDialogMessage:
        "There are Orders where the System shows a warning. Only continue if you are sure the warning will not result in an error when fulfilling the orders! ",
    };
  },
  computed: {
    ...mapGetters(["ordersToFulfill", "loadingOrders", "ticketsActive", "ordersToFulfillReady"]),
    sortedOrdersToFulfill() {
      const toMillis = d => (d ? Date.parse(d) || 0 : 0)
      const visible = (this.displayedUids
        ? this.ordersToFulfill.filter((o) => this.displayedUids[String(o.uid)])
        : this.ordersToFulfill
      ).filter((o) => !!o.fulfillArchived === this.archived)
      const sorted = [...visible].sort(
          (a, b) => toMillis(a.resultsSentDate) - toMillis(b.resultsSentDate)
      )
      // Express-Orders immer als eigener Block ganz oben; innerhalb beider
      // Gruppen bleibt die Sortierung nach resultsSentDate unverändert.
      return [
        ...sorted.filter((o) => o.isExpress),
        ...sorted.filter((o) => !o.isExpress),
      ]
    },
    headers() {
      // Kompakt (Malte 16.10.): Adresse in einer breiten Spalte, Alter + Mail-Datum zusammen, Notizen nur Icons
      return [
        { text: "Order", value: "orderName", width: 170 },
        { text: "", value: "orderNote", width: 64, sortable: false },
        { text: "Alter", value: "orderDate", width: 150 },
        { text: "Lieferadresse", value: "address", sortable: false },
        { text: "", value: "action", width: 180, sortable: false, align: "end" },
      ];
    },
    /** Bestellung für den Karten-Dialog frisch aus dem Store (Geprüft-Haken aktualisiert sich live) */
    mapOrderLive() {
      if (!this.mapOrder) return null;
      return this.ordersToFulfill.find((o) => String(o.uid) === String(this.mapOrder)) || null;
    },
    numberSelectedOrdersWithWarning() {
      return this.ordersToFulfill.filter((order) => {
        return this.selectedOrders.find(
          (selectedOrder) =>
            selectedOrder.uid === order.uid && order.showOrderWarning
        );
      }).length;
    },
    warnDialogTitle() {
      return `Fulfill ${this.numberSelectedOrdersWithWarning} ${
        this.numberSelectedOrdersWithWarning > 1 ? "Orders" : "Order"
      } with Warning`;
    },
    selectedOrder() {
      return this.ordersToFulfill.find(order => order.uid === this.selectedOrderId)
    }
  },
  watch: {
    ordersToFulfill: {
      immediate: true,
      handler(list) {
        this.syncDisplayed(list || [])
      },
    },
    // Store meldet: Erst-Snapshot komplett (inkl. Tasks) → ab jetzt puffern
    ordersToFulfillReady: {
      immediate: true,
      handler(ready) {
        if (ready) this.endInitialPhase()
        else this.initialPhase = true
      },
    },
  },
  mounted() {
    // Sicherheitsnetz: Erstladephase spätestens nach 60 s beenden
    this.initialDeadline = Date.now() + 60000
    // Auto-Einblenden: nichts angehakt und 5 min keine Interaktion
    this.autoMergeTimer = setInterval(() => {
      if (this.initialPhase && Date.now() > this.initialDeadline) this.endInitialPhase()
      if (!this.pendingNewUids.length) return
      if (this.selectedOrders.length) return
      if (Date.now() - this.lastInteractionAt < 5 * 60 * 1000) return
      this.mergePendingNew()
    }, 5000)
    // Rückkehr zum Tab: natürlicher Moment zum Einblenden
    document.addEventListener("visibilitychange", this.onVisibility)
  },
  beforeDestroy() {
    if (this.autoMergeTimer) clearInterval(this.autoMergeTimer)
    document.removeEventListener("visibilitychange", this.onVisibility)
  },
  methods: {
    noteInteraction() {
      this.lastInteractionAt = Date.now()
    },
    async setArchived(flag) {
      if (!this.selectedOrders.length || this.archiving) return
      this.archiving = true
      const n = this.selectedOrders.length
      try {
        const by = { uid: (auth.currentUser && auth.currentUser.uid) || null, name: (auth.currentUser && auth.currentUser.displayName) || "" }
        for (const o of this.selectedOrders) {
          await setDoc(doc(db, "orders", String(o.uid)), flag
            ? { fulfillArchived: true, fulfillArchivedAt: Date.now(), fulfillArchivedBy: by }
            : { fulfillArchived: false, fulfillArchivedAt: null, fulfillArchivedBy: null }, { merge: true })
        }
        this.selectedOrders = []
        EventBus.$emit(events.SNACKBAR, { message: flag ? `${n} Bestellung${n === 1 ? '' : 'en'} archiviert` : `${n} Bestellung${n === 1 ? '' : 'en'} wiederhergestellt`, type: 'success' })
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Archivieren fehlgeschlagen: ${e.message}`, type: 'error' })
      } finally {
        this.archiving = false
      }
    },
    archiveSelected() {
      return this.setArchived(true)
    },
    unarchiveSelected() {
      return this.setArchived(false)
    },
    onVisibility() {
      if (document.visibilityState === "visible" && this.pendingNewUids.length && !this.selectedOrders.length) this.mergePendingNew()
    },
    endInitialPhase() {
      if (!this.initialPhase) return
      // Archiv-Ansicht: nie puffern, immer alles zeigen
      if (this.archived) return
      this.initialPhase = false
      // Alles, was bis hierhin kam, ist der Ausgangsstand
      const map = {}
      this.ordersToFulfill.forEach((o) => (map[String(o.uid)] = true))
      this.displayedUids = map
      this.pendingNewUids = []
    },
    /**
     * Store-Liste → sichtbare Liste. Erstbefüllung übernimmt alles; danach landen
     * normale Neuzugänge im Puffer, Express sofort sichtbar (kurz hervorgehoben),
     * verschwundene Bestellungen gehen sofort raus (inkl. aus der Auswahl).
     */
    syncDisplayed(list) {
      const current = new Set(list.map((o) => String(o.uid)))
      // Erstladephase: der Store liefert die Bestellungen nacheinander (Tasks werden je Bestellung
      // nachgeladen) — alles übernehmen und erst puffern, wenn die Liste 1,5 s ruhig war.
      if (this.initialPhase) {
        const map = {}
        list.forEach((o) => (map[String(o.uid)] = true))
        this.displayedUids = map
        return
      }
      if (!this.displayedUids) this.displayedUids = {}
      const next = { ...this.displayedUids }
      let changed = false
      // Verschwundene (versendet, anderer Status)
      Object.keys(next).forEach((uid) => {
        if (!current.has(uid)) {
          delete next[uid]
          changed = true
        }
      })
      const pending = this.pendingNewUids.filter((uid) => current.has(uid))
      for (const o of list) {
        const uid = String(o.uid)
        if (next[uid] || pending.includes(uid)) continue
        if (o.isExpress) {
          next[uid] = true
          this.$set(this.recentlyAddedUids, uid, true)
          setTimeout(() => this.$delete(this.recentlyAddedUids, uid), 8000)
          changed = true
        } else {
          pending.push(uid)
        }
      }
      if (pending.length !== this.pendingNewUids.length || pending.some((u, i) => u !== this.pendingNewUids[i])) this.pendingNewUids = pending
      if (changed) this.displayedUids = next
      // Auswahl bereinigen: Bestellungen, die nicht mehr in der Liste sind
      const stillThere = this.selectedOrders.filter((o) => current.has(String(o.uid)))
      if (stillThere.length !== this.selectedOrders.length) {
        const gone = this.selectedOrders.length - stillThere.length
        this.selectedOrders = stillThere
        EventBus.$emit(events.SNACKBAR, { message: `${gone} angehakte Bestellung${gone === 1 ? '' : 'en'} ${gone === 1 ? 'ist' : 'sind'} inzwischen anders bearbeitet worden und wurde${gone === 1 ? '' : 'n'} aus der Auswahl entfernt`, type: 'warning' })
      }
    },
    mergePendingNew() {
      if (!this.pendingNewUids.length) return
      const next = { ...(this.displayedUids || {}) }
      this.pendingNewUids.forEach((uid) => {
        next[uid] = true
        this.$set(this.recentlyAddedUids, uid, true)
        setTimeout(() => this.$delete(this.recentlyAddedUids, uid), 8000)
      })
      this.displayedUids = next
      this.pendingNewUids = []
    },
    openOrder(uid) {
      openOrderModal(uid)
    },
    openAddressMap(order) {
      this.mapOrder = order.uid;
    },
    noHouseNumber(order) {
      const sa = order.shippingAddress || {};
      return !/\d/.test(`${sa.addressPrimary || ""} ${sa.addressSecondary || ""}`);
    },
    async addressChecked(order) {
      const orderDocRef = doc(db, "orders", order.uid);
      try {
        await setDoc(
            orderDocRef,
            {
              shippingAddress: {
                checked: !order.shippingAddress.checked
              }
            },
            { merge: true }
        );
      } catch (error) {
        console.error("Error updating order document:", error);
      }
    },
    showManualFulfillDialog() {
      this.showManualFulfillmentDialog = true;
    },
    closeManualFulfillmentDialog() {
      this.showManualFulfillmentDialog = false;
    },
    openItemMenu(e, orderId) {
      e.preventDefault();
      this.selectedOrderId = orderId
      this.showMenu = false;
      this.x = e.clientX;
      this.y = e.clientY;
      this.$nextTick(() => {
        this.showMenu = true;
      });
    },
    checkAndFulfillSelectedOrders() {
      if (this.numberSelectedOrdersWithWarning) {
        this.showWarnDialog = true;
      } else {
        this.fulfillSelectedOrders();
      }
    },
    editOrder(order) {
      this.orderToEdit = order;
      this.showEditOrderDialog = true;
    },
    searchOrderInMails() {
      window.open(
        `https://mail.google.com/mail/u/?authuser=lisa@liebespferd.com&tab=wm#search/%23${
          this.selectedOrder.orderName.split("#")[1]
        }`,
        "_blank"
      );
    },
    dateFromNow(date) {
      return date ? getDateFromNow(date) : "N/A"
    },
    // Grund des Holds für den Hover am Pause-Icon
    holdReason(item) {
      if (item.resolveOnNewAddress) return "Wartet auf neue Adresse (in der Versandliste gesetzt). Löst sich mit Adressbestätigung oder Adressänderung.";
      const t = (this.ticketsActive || []).find((x) => String(x.orderUid) === String(item.uid));
      if (t) {
        const held = Object.values(t.quelpTickets || {}).filter((q) => q.onHold);
        if (held.length) return "Quelp-Ticket on hold: " + held.map((q) => q.subject || "Ohne Betreff").join(" · ") + ". Lösen im Ticket.";
        if (Object.keys(t.data || {}).length) return "Status-Seiten-Vorgang offen (" + Object.keys(t.data).join(", ") + ").";
      }
      return "Support-Hold an der Bestellung. Lösen in der Support-Ansicht.";
    },
    closeEditOrderDialog() {
      this.showEditOrderDialog = false;
      this.orderToEdit = null;
    },
    closeIsFulfillingDialog() {
      this.showIsFulfillingDialog = false;
    },
    closeWarnDialog() {
      this.showWarnDialog = false;
    },
    async markOrderForAwaitingAddress() {
      const orderDoc = doc(db, "orders", this.selectedOrder.uid);
      if (!this.selectedOrder.supportPending) {
        await setDoc(orderDoc,{
          supportPending: true,
          resolveOnNewAddress: true
        }, { merge: true })
      }
    },
    async fulfillSelectedOrders() {
      this.showWarnDialog = false;
      this.showIsFulfillingDialog = true;
      for await (const order of this.selectedOrders) {
        const orderDocRef = doc(db, "orders", order.uid);
        try {
          await setDoc(
            orderDocRef,
            {
              status: orderStatus.WAITING_FOR_FULFILL,
              fulfillTime: Date.now(),
            },
            { merge: true }
          );
          this.selectedOrders = this.selectedOrders.filter(
            (o) => o.uid !== order.uid
          );
        } catch (error) {
          console.error("Error updating order document:", error);
        }
      }
      this.showIsFulfillingDialog = false;
    },
    orderHasAllTasksSatisfied(tasks) {
      return (tasks.filter(task => task.feedback && task.feedback.satisfied)).length === tasks.length
    },
    orderHasTasksUnsatisfied(tasks) {
      return tasks.some(task => task.feedback && !task.feedback.satisfied)
    },
    getRowClass(item) {
      // Express-Marker zusätzlich zur Ampel; die Ampel-Farben (weiter unten
      // im Stylesheet definiert) behalten bei Konflikt den Vorrang.
      const fresh = this.recentlyAddedUids[String(item.uid)] ? 'fresh-row ' : ''
      const express = (item.isExpress ? 'express-row' : '') + (fresh ? ' ' + fresh.trim() : '')
      if (this.orderHasTasksUnsatisfied(item.tasks)) {
        return `${express} dark-red-row`
      } else if (item.orderNote) {
        return `${express} red-row`
      } else if (item.supportPending || item.fulfillHold || item.resolveOnNewAddress) {
        // Wartet auf Kunde (Support-Hold, manueller Hold, Adress-Anfrage) → grau wie jeder Hold
        return `${express} grey-row`
      } else if (item.showOrderWarning) {
        return `${express} orange-row`
      } else if (this.orderHasAllTasksSatisfied(item.tasks)) {
        return `${express} green-row`
      } else {
        return express
      }
    },
    disableRowSelect(item) {
      return item.orderNote || item.fulfillHold
    }
  },
};
</script>

<style lang="scss">
/* Zeilen-Ampel — kräftig genug, um sich auf einen Blick zu unterscheiden (13.10.: die Pastelltöne
   waren fast weiß). Fläche + Balken links. Express hat KEINE eigene Zeilenfarbe mehr (Malte 14.10.):
   Blitz-Icon und gelbe Bestellnummer reichen, die Zeile folgt der Ampel wie jede andere. */
.orange-row {
  background-color: #ffd9a8 !important;          /* Adresse prüfen: kräftiges Orange */
  box-shadow: inset 6px 0 0 #ea580c;
}

.red-row {
  background-color: #ffd0d0 !important;          /* Order-Note */
  box-shadow: inset 6px 0 0 #ef4444;
}

.dark-red-row {
  background-color: #ffb3b3 !important;          /* Kunde unzufrieden */
  box-shadow: inset 6px 0 0 #991b1b;
}

.green-row {
  background-color: #c9f2cf !important;          /* Daumen hoch: sattes Grün */
  box-shadow: inset 6px 0 0 #16a34a;
}

.grey-row {
  background-color: #e5e7eb !important;          /* Hold / Support */
  box-shadow: inset 6px 0 0 #6b7280;
  opacity: 0.8;
}

.control-header {
  position: fixed;
  top: 76px;
  left: 50%;
  transform: translateX(-50%);
  width: min(720px, calc(100vw - 120px));
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10;
  padding: 10px 12px 10px 22px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 8px 30px rgba(16, 24, 40, 0.18);
  transition: opacity ease-in 0.25s, transform ease-in 0.25s;
  opacity: 1;

  &--hide {
    opacity: 0;
    pointer-events: none;
    transform: translateX(-50%) translateY(-8px);
  }

  &__selected-items {
    font-size: 14px;
    font-weight: 700;
  }
  &__textBtn {
    background: transparent !important;
    color: #6a4fc4 !important;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 700;
    margin-left: auto;
    margin-right: 12px;
  }

  .v-btn {
    border-radius: 999px;
    background: #7b5cd6 !important;
    color: #fff !important;
  }
}

.orders-to-fulfill .v-data-table .v-data-table__wrapper table {
  width: 100%;
}
.new-orders-bar {
  position: sticky; top: 0; z-index: 3;
  display: flex; align-items: center; flex-wrap: wrap; gap: 4px;
  margin: 0 0 8px; padding: 8px 14px; border-radius: 999px;
  background: #edeafa; color: #4b3a9a; font-size: 13px; font-weight: 600;
  box-shadow: 0 4px 14px rgba(16, 24, 40, 0.08);
  &__hint { margin-left: auto; font-size: 11px; font-weight: 400; color: rgba(0, 0, 0, 0.5); }
}
.orders-to-fulfill .v-data-table tr.fresh-row td {
  animation: fresh-row-fade 8s ease-out forwards;
}
.orders-to-fulfill .v-data-table tr.fresh-row td:first-child {
  box-shadow: inset 4px 0 0 #7b5cd6;
}
@keyframes fresh-row-fade {
  0% { background-color: rgba(123, 92, 214, 0.18); }
  100% { background-color: transparent; }
}

.orders-to-fulfill .v-data-table .v-data-table__wrapper table tr td:last-child,
.orders-to-fulfill .v-data-table .v-data-table__wrapper table tr th:last-child {
  position: sticky;
  right: 0;
  z-index: 1;
  /* From https://css.glass */
  background: rgba(255, 255, 255, 0.36);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13.1px);
  -webkit-backdrop-filter: blur(13.1px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.addr-cell {
  cursor: pointer;
  padding: 4px 0;
  line-height: 1.35;
  &__line1 { font-size: 14px; }
  &__line2 { font-size: 13px; color: rgba(0, 0, 0, 0.75); display: flex; flex-wrap: wrap; align-items: center; gap: 4px; }
  &__muted { color: rgba(0, 0, 0, 0.55); }
  &__sep { color: rgba(0, 0, 0, 0.35); }
  &__cc { font-size: 11px; font-weight: 700; background: rgba(0, 0, 0, 0.06); border-radius: 4px; padding: 0 5px; margin-left: 2px; }
  &:hover &__line2 { text-decoration: underline; text-decoration-color: rgba(0, 0, 0, 0.25); }
}
.age-cell {
  &__mail { font-size: 11px; color: rgba(0, 0, 0, 0.5); margin-top: 2px; white-space: nowrap; }
}
.express-order-name {
  color: #9a7b00 !important;   /* dunkler als vorher: auf Gelb/Grün/Orange lesbar */
  font-weight: bolder;
}
.order-name-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
</style>
